import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { CloudOff, Lock, Wifi } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { handleAuthFailure, useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { EmptyState, Spinner } from "@/components/ui/Feedback";
import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { invalidateLedger, keys } from "@/lib/queries";
import { closeLocalDatabase, destroyLocalDatabase, openLocalDatabase } from "./database";
import type { PushResponse } from "./outbox";
import type { PullPage } from "./repo";
import { setActiveEngine, startAutoSync, SyncEngine, type SyncState } from "./sync";

type Phase = "idle" | "opening" | "ready" | "locked" | "failed";

interface LocalValue {
  phase: Phase;
  error: string | null;
  /** False when the browser refused persistent storage: works now, does not survive a reload. */
  persistent: boolean;
  engine: SyncEngine | null;
}

const LocalContext = createContext<LocalValue>({ phase: "idle", error: null, persistent: true, engine: null });
export const useLocal = () => useContext(LocalContext);

const IDLE_SYNC: SyncState = { status: "idle", initialSyncDone: false, lastSyncAt: null, error: null, applied: 0 };
const noSubscribe = () => () => undefined;

export function useSyncState(): SyncState {
  const { engine } = useLocal();
  return useSyncExternalStore(engine ? engine.subscribe : noSubscribe, engine ? engine.getSnapshot : () => IDLE_SYNC);
}

const onlineSubscribe = (notify: () => void) => {
  window.addEventListener("online", notify);
  window.addEventListener("offline", notify);
  return () => {
    window.removeEventListener("online", notify);
    window.removeEventListener("offline", notify);
  };
};
/** The browser's own idea of connectivity. Instant, but only a hint: a request is the real test. */
export const useOnline = (): boolean => useSyncExternalStore(onlineSubscribe, () => navigator.onLine, () => true);

/** Screens that read from the replica re-read as soon as rows land. */
function refreshLocalReaders(qc: QueryClient): void {
  for (const key of [keys.accounts, keys.categories, ["merchants"], ["transactions"], ["transaction"], keys.outbox]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

/**
 * Opens the signed-in user's device database and keeps it synced. Must sit inside AuthProvider.
 */
export function LocalProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id ?? null;
  const [value, setValue] = useState<LocalValue>({ phase: "idle", error: null, persistent: true, engine: null });

  useEffect(() => {
    if (!userId) {
      setActiveEngine(null);
      setValue({ phase: "idle", error: null, persistent: true, engine: null });
      void closeLocalDatabase();
      return undefined;
    }

    let cancelled = false;
    let stop: (() => void) | undefined;
    setValue((v) => ({ ...v, phase: "opening", error: null }));

    void (async () => {
      const outcome = await openLocalDatabase(userId);
      if (cancelled) return;
      if (outcome.status === "locked") return setValue((v) => ({ ...v, phase: "locked" }));
      if (outcome.status === "failed") return setValue((v) => ({ ...v, phase: "failed", error: outcome.error }));

      const { driver, persistent } = outcome.db;
      const engine = new SyncEngine(driver, {
        fetchPage: (cursor, limit) => api.get<PullPage>("/sync/pull", { cursor, limit }),
        pushOps: (ops) => api.post<PushResponse>("/sync/push", { device_id: getDeviceId(), ops }),
        onData: () => refreshLocalReaders(qc),
        // Balances, budgets and reports are computed by the server: refetch them once something changed.
        onDone: (changed) => {
          if (changed) invalidateLedger(qc);
        },
        onUnauthorized: (error) => handleAuthFailure(qc, error),
      });
      await engine.init();
      if (cancelled) return;
      setActiveEngine(engine);
      setValue({ phase: "ready", error: null, persistent, engine });
      stop = startAutoSync(engine);
    })();

    return () => {
      cancelled = true;
      stop?.();
      setActiveEngine(null);
    };
  }, [userId, qc]);

  return <LocalContext.Provider value={value}>{children}</LocalContext.Provider>;
}

/**
 * Holds the app back until the device has its data: the local database is open and the first
 * full download has finished. After that the app opens instantly, online or not.
 */
export function LocalGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { phase, error, engine } = useLocal();
  const sync = useSyncState();
  const online = useOnline();

  if (phase === "idle" || phase === "opening") return <Spinner label="Opening your ledger" />;

  if (phase === "locked") {
    return (
      <div className="mx-auto max-w-md py-16">
        <EmptyState
          icon={<Lock className="size-8" />}
          title="Ledger is open in another window"
          body="Your data on this device can be used by one window at a time. Close the other window or tab, then reload this one."
          action={<Button onClick={() => window.location.reload()}>Reload</Button>}
        />
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="mx-auto max-w-md py-16">
        <EmptyState
          icon={<CloudOff className="size-8" />}
          title="Can't open this device's storage"
          body={`${error ?? "Unknown error"}. Your data is safe on the server: resetting this device's copy downloads it again.`}
          action={
            <Button
              variant="primary"
              onClick={() => {
                const reset = user ? destroyLocalDatabase(user.id).catch(() => undefined) : Promise.resolve();
                void reset.then(() => window.location.reload());
              }}
            >
              Reset and download again
            </Button>
          }
        />
      </div>
    );
  }

  if (!sync.initialSyncDone) {
    if (sync.status === "offline" || !online) {
      return (
        <div className="mx-auto max-w-md py-16">
          <EmptyState
            icon={<CloudOff className="size-8" />}
            title="Connect to finish setting up"
            body="The first time you use Ledger on a device, it downloads your data. That needs an internet connection — after that it works offline."
            action={<Button onClick={() => void engine?.sync()}>Try again</Button>}
          />
        </div>
      );
    }
    if (sync.status === "error") {
      return (
        <div className="mx-auto max-w-md py-16">
          <EmptyState
            icon={<Wifi className="size-8" />}
            title="Couldn't download your data"
            body={sync.error ?? "Something went wrong."}
            action={<Button variant="primary" onClick={() => void engine?.sync()}>Try again</Button>}
          />
        </div>
      );
    }
    return (
      <Spinner
        label={sync.applied > 0 ? `Downloading your ledger (${sync.applied.toLocaleString()} records)` : "Setting up your ledger on this device"}
      />
    );
  }

  return <>{children}</>;
}
